import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { config } from '../../config';
import { formatDatePSTShort } from '../../utils/dateUtils';
import './BillingDashboard.css';

interface Payment {
  id: string;
  invoice_id: string;
  invoice_number?: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  receipt_number?: string;
  notes?: string;
}

interface PaymentHistoryProps {
  invoiceId?: string;
  studentId?: string;
  parentId?: string;
  onExport?: () => void;
}

const PaymentHistory: React.FC<PaymentHistoryProps> = ({
  invoiceId,
  studentId,
  parentId,
  onExport
}) => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    method: 'all'
  });

  useEffect(() => {
    fetchPayments();
  }, [invoiceId, studentId, parentId]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      let url = '';
      if (invoiceId) {
        url = `${config.BACKEND_URL}/api/billing/payments/invoice/${invoiceId}`;
      } else {
        // For now, we'll need to get payments through invoices
        // This is a placeholder - actual implementation depends on API structure
        url = `${config.BACKEND_URL}/api/billing/payments`;
      }
      
      const response = await axios.get(url, { headers });
      setPayments(response.data || []);
    } catch (err: any) {
      console.error('Error fetching payments:', err);
      setError(err.response?.data?.detail || 'Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (onExport) {
      onExport();
    } else {
      // Default CSV export
      const csvContent = [
        ['Date', 'Amount', 'Method', 'Invoice', 'Receipt Number'].join(','),
        ...payments.map(p => [
          formatDatePSTShort(p.payment_date),
          p.amount.toFixed(2),
          p.payment_method,
          p.invoice_number || p.invoice_id,
          p.receipt_number || ''
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payments-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const filteredPayments = payments.filter(payment => {
    if (filters.dateFrom && payment.payment_date < filters.dateFrom) {
      return false;
    }
    if (filters.dateTo && payment.payment_date > filters.dateTo) {
      return false;
    }
    if (filters.method !== 'all' && payment.payment_method !== filters.method) {
      return false;
    }
    return true;
  });

  const getPaymentMethodLabel = (method: string) => {
    const labels: { [key: string]: string } = {
      'stripe': 'Credit Card',
      'cash': 'Cash',
      'cheque': 'Cheque',
      'bank_transfer': 'Bank Transfer',
      'e_transfer': 'E-Transfer'
    };
    return labels[method] || method;
  };

  if (loading) {
    return <div className="loading">Loading payment history...</div>;
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  return (
    <div className="payment-history-container">
      <div className="payment-history-header">
        <h3>Payment History</h3>
        <button className="btn btn-secondary btn-sm" onClick={handleExport}>
          Export CSV
        </button>
      </div>
      
      <div className="payment-history-filters">
        <div className="filter-group">
          <label>From Date:</label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
            className="filter-input"
          />
        </div>
        
        <div className="filter-group">
          <label>To Date:</label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
            className="filter-input"
          />
        </div>
        
        <div className="filter-group">
          <label>Method:</label>
          <select
            value={filters.method}
            onChange={(e) => setFilters({ ...filters, method: e.target.value })}
            className="filter-select"
          >
            <option value="all">All Methods</option>
            <option value="stripe">Credit Card</option>
            <option value="cash">Cash</option>
            <option value="cheque">Cheque</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="e_transfer">E-Transfer</option>
          </select>
        </div>
      </div>
      
      {filteredPayments.length === 0 ? (
        <div className="empty-state">
          <p>No payments found</p>
        </div>
      ) : (
        <div className="payment-history-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Invoice</th>
                <th>Receipt Number</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map(payment => (
                <tr key={payment.id}>
                  <td>{formatDatePSTShort(payment.payment_date)}</td>
                  <td>${Number(payment.amount).toFixed(2)}</td>
                  <td>{getPaymentMethodLabel(payment.payment_method)}</td>
                  <td>{payment.invoice_number || payment.invoice_id}</td>
                  <td>{payment.receipt_number || '-'}</td>
                  <td>{payment.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;

